import { ListResponse } from '@/types/common';
import {
  User,
  MailboxPayload,
  Mailbox,
  InviteFormData,
  PhoneLine,
  UserLogin,
  UserSummary,
  Role,
} from '@/types/user';
import fetchWithAuth from './base';
import { getBrowserTimezone } from '@/app/lib/utils';

export class LoginApiError extends Error {
  status?: number;
  statusText?: string;
  details?: unknown;
  code?: string;
  constructor(
    message: string,
    options?: {
      status?: number;
      statusText?: string;
      details?: any;
      code?: string;
    }
  ) {
    super(message);
    this.name = 'LoginApiError';
    this.status = options?.status;
    this.statusText = options?.statusText;
    this.details = options?.details;
    this.code = options?.code;
  }
}

export const login = async (id_token: string, partner?: string): Promise<UserLogin> => {
  const response = await fetch('/api/user/login', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ id_token, ...(partner ? { partner } : {}) }),
  });
  const data = await response.json();

  if (!response.ok) {
    const message =
      data?.details?.error?.message || data?.message || data?.error || response.statusText;

    throw new LoginApiError(message, {
      status: response.status,
      statusText: response.statusText,
      details: data?.details,
      code: data?.details?.error?.code,
    });
  }

  return data;
};

export const getUserSummary = async (): Promise<UserSummary> => {
  const response = await fetchWithAuth('/api/user_summary', {
    method: 'GET',
  });
  return response.json();
};

export const updateUserSummary = async (
  id: string,
  data: Partial<UserSummary>
): Promise<UserSummary> => {
  const response = await fetchWithAuth(`/api/user_summary/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  return response.json();
};

export const getUserList = async ({
  clientId,
  startingAfter,
  endingBefore,
  limit,
  search,
  status,
  showInternal,
  filters,
}: {
  clientId: string;
  startingAfter?: string;
  endingBefore?: string;
  limit?: number;
  search?: string;
  status?: string;
  showInternal?: boolean;
  filters?: Array<{ field: string; operator: string; value: unknown }>;
}): Promise<ListResponse<User>> => {
  const requestFilters = [
    ...(filters || []),
    ...(search ? [{ field: 'search', operator: 'eq', value: search }] : []),
    ...(status ? [{ field: 'status', operator: 'eq', value: status }] : []),
    ...(showInternal ? [{ field: 'user_type', operator: 'eq', value: 'internal' }] : []),
  ];

  if (requestFilters.length) {
    const response = await fetchWithAuth(`/api/client/${clientId}/user/search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        filter: { match: 'all', rules: requestFilters },
        ...(startingAfter ? { starting_after: startingAfter } : {}),
        ...(endingBefore ? { ending_before: endingBefore } : {}),
        ...(limit ? { limit } : {}),
      }),
    });
    return response.json();
  }

  const params = new URLSearchParams();

  if (startingAfter) {
    params.append('starting_after', startingAfter);
  }

  if (endingBefore) {
    params.append('ending_before', endingBefore);
  }

  if (limit) {
    params.append('limit', limit.toString());
  }

  const response = await fetchWithAuth(`/api/client/${clientId}/user?${params.toString()}`, {
    method: 'GET',
  });
  return response.json();
};

export const getUserCount = async (
  clientId: string,
  filters?: Array<{ field: string; operator: string; value: unknown }>
): Promise<{
  total: number;
  active: number;
  pending: number;
  suspended: number;
  deleted: number;
}> => {
  const response = await fetchWithAuth(`/api/client/${clientId}/user/count`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(filters?.length ? { filter: { match: 'all', rules: filters } } : {}),
  });
  return response.json();
};

export const deleteUser = async (clientId: string, userId: string): Promise<void> => {
  await fetchWithAuth(`/api/client/${clientId}/user/${userId}`, {
    method: 'DELETE',
  });
};

export const restoreUser = async (clientId: string, userId: string): Promise<void> => {
  await fetchWithAuth(`/api/client/${clientId}/user/${userId}/restore`, {
    method: 'POST',
  });
};

export const getClientRoles = async (clientId: string): Promise<ListResponse<Role>> => {
  const response = await fetchWithAuth(`/api/client/${clientId}/role`, {
    method: 'GET',
  });
  return response.json();
};

export const inviteUser = async (data: InviteFormData): Promise<User> => {
  const payload = {
    ...data,
    type: data.type || 'external',
  };
  const response = await fetchWithAuth('/api/user/invite', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return response.json();
};

export const inviteAgencyUser = async (
  data: InviteFormData & { agency_id: string }
): Promise<User> => {
  const payload = {
    ...data,
    type: 'agency',
  };
  const response = await fetchWithAuth(`/api/user/invite`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return response.json();
};

export const getUser = async (clientId: string, id: string): Promise<User> => {
  const response = await fetchWithAuth(`/api/client/${clientId}/user/${id}`, {
    method: 'GET',
  });
  return response.json();
};

export const updateUser = async (
  clientId: string,
  id: string,
  data: Partial<User>
): Promise<User> => {
  const response = await fetchWithAuth(`/api/client/${clientId}/user/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
  return response.json();
};

export const updateUserTimezone = async (
  clientId: string,
  id: string,
  viewTimezone: string
): Promise<User> => {
  const response = await fetchWithAuth(`/api/client/${clientId}/user/${id}/time_zone`, {
    method: 'PATCH',
    body: JSON.stringify({ view_timezone: viewTimezone }),
  });
  return response.json();
};

export const getUserWithTimezone = async (clientId: string, id: string): Promise<User> => {
  const user = await getUser(clientId, id);

  if (user.view_timezone) {
    return user;
  }

  return updateUserTimezone(clientId, id, getBrowserTimezone());
};

export const getUserMailboxes = async (
  clientId: string,
  userId: string
): Promise<ListResponse<Mailbox>> => {
  const response = await fetchWithAuth(`/api/client/${clientId}/user/${userId}/mailbox`, {
    method: 'GET',
  });
  return response.json();
};

export const connectMailbox = async (
  clientId: string,
  userId: string,
  data: {
    id_token: string;
  }
): Promise<Mailbox> => {
  const response = await fetchWithAuth(`/api/client/${clientId}/user/${userId}/mailbox/connect`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.json();
};

export const disconnectMailbox = async (
  clientId: string,
  userId: string,
  mailboxId: string
): Promise<Mailbox> => {
  const response = await fetchWithAuth(
    `/api/client/${clientId}/user/${userId}/mailbox/${mailboxId}/disconnect`,
    {
      method: 'POST',
    }
  );
  return response.json();
};

export const deleteMailbox = async (clientId: string, userId: string, mailboxId: string) => {
  const response = await fetchWithAuth(
    `/api/client/${clientId}/user/${userId}/mailbox/${mailboxId}`,
    {
      method: 'DELETE',
    }
  );
  if (response.status === 204) {
    return null;
  }
  return response.json();
};

export const getClientPhoneLines = async (
  clientId: string,
  status?: string
): Promise<ListResponse<PhoneLine>> => {
  const params = new URLSearchParams();
  if (status) {
    params.append('status', status);
  }
  const response = await fetchWithAuth(`/api/client/${clientId}/phone_line?${params.toString()}`, {
    method: 'GET',
  });
  return response.json();
};

export const getUserPhoneLines = async (
  clientId: string,
  userId: string
): Promise<ListResponse<PhoneLine>> => {
  const response = await fetchWithAuth(`/api/client/${clientId}/user/${userId}/phone_line`, {
    method: 'GET',
  });
  return response.json();
};

export const addUserPhoneLine = async (
  clientId: string,
  userId: string,
  data:
    | { type: 'available_line' }
    | {
        type: 'custom_line';
        area_code: number;
      }
): Promise<PhoneLine> => {
  const response = await fetchWithAuth(`/api/client/${clientId}/user/${userId}/phone_line`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.json();
};

export const disconnectUserPhoneLine = async (
  clientId: string,
  userId: string,
  phoneLineId: string
): Promise<PhoneLine> => {
  const response = await fetchWithAuth(
    `/api/client/${clientId}/user/${userId}/phone_line/${phoneLineId}/disconnect`,
    {
      method: 'POST',
    }
  );
  return response.json();
};

export const updateUserPhoneLine = async (
  clientId: string,
  userId: string,
  phoneLineId: string,
  data: { phone_number?: string; message_limits?: { new_conversations_per_day: number } }
): Promise<PhoneLine> => {
  const response = await fetchWithAuth(
    `/api/client/${clientId}/user/${userId}/phone_line/${phoneLineId}`,
    {
      method: 'PATCH',
      body: JSON.stringify(data),
    }
  );
  return response.json();
};

export const deleteUserPhoneLine = async (
  clientId: string,
  userId: string,
  phoneLineId: string
) => {
  const response = await fetchWithAuth(
    `/api/client/${clientId}/user/${userId}/phone_line/${phoneLineId}`,
    {
      method: 'DELETE',
    }
  );
  if (response.status === 204) return null;
  return response.json();
};

export const transferUserPhoneLine = async (
  clientId: string,
  userId: string,
  phoneLineId: string,
  data: { new_user_id: string }
): Promise<PhoneLine> => {
  const response = await fetchWithAuth(
    `/api/client/${clientId}/user/${userId}/phone_line/${phoneLineId}/transfer`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );
  return response.json();
};

export const uploadUserProfilePic = async (
  clientId: string,
  userId: string,
  file: File
): Promise<User> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await fetchWithAuth(`/api/client/${clientId}/user/${userId}/profile_pic`, {
    method: 'POST',
    body: formData,
  });
  return response.json();
};

export const completeUserEngagementConfig = async (
  clientId: string,
  userId: string
): Promise<void> => {
  await fetchWithAuth(`/api/client/${clientId}/user/${userId}/engagement_config`, {
    method: 'PATCH',
    body: JSON.stringify({ status: 'completed' }),
  });
};

export const updateUserMailbox = async (
  clientId: string,
  userId: string,
  mailboxId: string,
  data: MailboxPayload
): Promise<Mailbox> => {
  const response = await fetchWithAuth(
    `/api/client/${clientId}/user/${userId}/mailbox/${mailboxId}`,
    {
      method: 'PATCH',
      body: JSON.stringify(data),
    }
  );
  return response.json();
};
